/**
 * Per-integration metadata: labels, descriptions, field definitions,
 * icons, and plain-English setup hints.
 */
import React from 'react';
import type { IntegrationKind } from '../api/integrations.js';

export interface FieldDef {
  key: string;
  label: string;
  type: 'text' | 'password' | 'url' | 'number';
  placeholder?: string;
  hint?: string;
  required?: boolean;
  isSecret?: boolean;
}

export interface IntegrationMeta {
  kind: IntegrationKind;
  label: string;
  description: string;
  /** Short blurb for the card */
  summary: string;
  icon: string; // emoji fallback
  color: string; // tailwind bg class for icon backdrop
  docsUrl?: string;
  configFields: FieldDef[];
  secretField: FieldDef;
  setupHint: React.ReactNode;
}

const INTEGRATIONS_META: IntegrationMeta[] = [
  {
    kind: 'connectwise',
    label: 'ConnectWise Manage',
    description: 'System of record for tickets, members, time entries, and scheduling.',
    summary: 'Tickets · Members · Time · Schedule',
    icon: '🔧',
    color: 'bg-orange-100 dark:bg-orange-900/30',
    docsUrl: 'https://developer.connectwise.com',
    configFields: [
      {
        key: 'baseUrl',
        label: 'API Base URL',
        type: 'url',
        placeholder: 'https://na.myconnectwise.net/v4_6_release/apis/3.0',
        hint: 'North-America region uses na.myconnectwise.net',
        required: true,
      },
      {
        key: 'companyId',
        label: 'Company ID',
        type: 'text',
        placeholder: 'yourcompany',
        hint: 'The short company identifier you log in with',
        required: true,
      },
      {
        key: 'publicKey',
        label: 'Public Key',
        type: 'text',
        placeholder: 'xxxxxxxxxxxxxxxx',
        required: true,
      },
      {
        key: 'clientId',
        label: 'Client ID',
        type: 'text',
        placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
        hint: 'Register at developer.connectwise.com → My Apps',
        required: true,
      },
    ],
    secretField: {
      key: 'privateKey',
      label: 'Private Key',
      type: 'password',
      placeholder: '••••••••',
      hint: 'Never shared after save. Generate under System → Members → API Keys.',
      required: true,
      isSecret: true,
    },
    setupHint: (
      <ol className="space-y-1.5 text-sm text-content-subtle list-decimal list-inside">
        <li>In ConnectWise, go to <strong>System → Members → API Keys</strong>.</li>
        <li>Click <strong>+</strong> to create a new key. Copy both the Public and Private keys — the private key is only shown once.</li>
        <li>Register a Client ID at <strong>developer.connectwise.com → My Apps</strong> (use any callback URL).</li>
        <li>Paste all values above. The base URL is usually <code>https://na.myconnectwise.net/v4_6_release/apis/3.0</code>.</li>
      </ol>
    ),
  },
  {
    kind: 'microsoft365',
    label: 'Microsoft 365',
    description: 'Graph API for calendars, users, and scheduling availability.',
    summary: 'Calendar · Availability · Users',
    icon: '📅',
    color: 'bg-blue-100 dark:bg-blue-900/30',
    docsUrl: 'https://learn.microsoft.com/graph',
    configFields: [
      {
        key: 'tenantId',
        label: 'Entra Tenant ID',
        type: 'text',
        placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
        hint: 'Found in Entra ID → Overview',
        required: true,
      },
      {
        key: 'clientId',
        label: 'App (Client) ID',
        type: 'text',
        placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
        required: true,
      },
    ],
    secretField: {
      key: 'clientSecret',
      label: 'Client Secret',
      type: 'password',
      placeholder: '••••••••',
      hint: 'Created under App Registrations → Certificates & secrets.',
      required: true,
      isSecret: true,
    },
    setupHint: (
      <ol className="space-y-1.5 text-sm text-content-subtle list-decimal list-inside">
        <li>Go to <strong>portal.azure.com → Entra ID → App registrations</strong> and create a new registration.</li>
        <li>Under <strong>API permissions</strong>, add: Calendars.Read, User.Read.All, Calendars.ReadWrite (Application type).</li>
        <li>Grant admin consent for your organization.</li>
        <li>Under <strong>Certificates &amp; secrets</strong>, create a new client secret and copy its value immediately.</li>
      </ol>
    ),
  },
  {
    kind: 'teams',
    label: 'Microsoft Teams Bot',
    description: 'Teams bot for dispatcher notifications, NT commands, and approvals.',
    summary: 'Bot · Notifications · Dispatch',
    icon: '💬',
    color: 'bg-purple-100 dark:bg-purple-900/30',
    configFields: [
      {
        key: 'clientId',
        label: 'App (Client) ID',
        type: 'text',
        placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
        required: true,
      },
      {
        key: 'tenantId',
        label: 'Entra Tenant ID',
        type: 'text',
        placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
        required: true,
      },
      {
        key: 'defaultChannelId',
        label: 'Default Channel ID (optional)',
        type: 'text',
      },
    ],
    secretField: {
      key: 'clientSecret',
      label: 'Client Secret',
      type: 'password',
      placeholder: '••••••••',
      required: true,
      isSecret: true,
    },
    setupHint: (
      <ol className="space-y-1.5 text-sm text-content-subtle list-decimal list-inside">
        <li>In Azure, go to <strong>Azure Bot</strong> and create a new bot resource.</li>
        <li>Under <strong>Configuration</strong>, set the messaging endpoint to <code>https://techsysconnect.com/api/teams/messages</code>.</li>
        <li>Copy the <strong>Microsoft App ID</strong> and create a client secret under <strong>Manage Password</strong>.</li>
        <li>Add the bot to your Teams tenant via the Teams Admin Center.</li>
      </ol>
    ),
  },
  {
    kind: 'threecx',
    label: '3CX Phone System',
    description: 'Call intake: voicemail transcription and ticket creation from inbound calls.',
    summary: 'Calls · Voicemail · Transcription',
    icon: '📞',
    color: 'bg-green-100 dark:bg-green-900/30',
    configFields: [
      {
        key: 'baseUrl',
        label: '3CX Base URL',
        type: 'url',
        placeholder: 'https://pbx.yourcompany.com',
        required: true,
      },
      {
        key: 'clientId',
        label: 'Client ID',
        type: 'text',
        required: true,
      },
    ],
    secretField: {
      key: 'clientSecret',
      label: 'Client Secret',
      type: 'password',
      placeholder: '••••••••',
      required: true,
      isSecret: true,
    },
    setupHint: (
      <ol className="space-y-1.5 text-sm text-content-subtle list-decimal list-inside">
        <li>In the 3CX Management Console, go to <strong>Settings → API</strong>.</li>
        <li>Create an API user with read access to calls and voicemail.</li>
        <li>Note the management console host and port. The default API port is <strong>5000</strong>.</li>
      </ol>
    ),
  },
  {
    kind: 'ninjarmm',
    label: 'NinjaRMM',
    description: 'RMM platform — device alerts trigger NOC tickets automatically.',
    summary: 'Devices · Alerts · Automation',
    icon: '🖥️',
    color: 'bg-cyan-100 dark:bg-cyan-900/30',
    configFields: [
      {
        key: 'region',
        label: 'Region',
        type: 'text',
        placeholder: 'us (or eu, oc)',
        hint: 'Determines your NinjaRMM API endpoint',
        required: true,
      },
      {
        key: 'clientId',
        label: 'OAuth Client ID',
        type: 'text',
        required: true,
      },
    ],
    secretField: {
      key: 'clientSecret',
      label: 'OAuth Client Secret',
      type: 'password',
      placeholder: '••••••••',
      required: true,
      isSecret: true,
    },
    setupHint: (
      <ol className="space-y-1.5 text-sm text-content-subtle list-decimal list-inside">
        <li>In NinjaRMM, go to <strong>Administration → Apps → API</strong>.</li>
        <li>Click <strong>Add</strong> and select <strong>API Services (machine-to-machine)</strong>.</li>
        <li>Grant scopes: monitoring, management (read). Copy the Client ID and Client Secret.</li>
      </ol>
    ),
  },
  {
    kind: 'sophos',
    label: 'Sophos Central',
    description: 'Security platform — alerts route to the SOC board.',
    summary: 'Threats · Alerts · SOC',
    icon: '🛡️',
    color: 'bg-red-100 dark:bg-red-900/30',
    configFields: [
      {
        key: 'tenantId',
        label: 'Sophos Tenant ID',
        type: 'text',
        placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
        required: true,
      },
      {
        key: 'region',
        label: 'Data Region',
        type: 'text',
        placeholder: 'us-west-2',
        hint: 'Found in Sophos Central → Account Details',
        required: true,
      },
      {
        key: 'clientId',
        label: 'API Client ID',
        type: 'text',
        required: true,
      },
    ],
    secretField: {
      key: 'clientSecret',
      label: 'API Client Secret',
      type: 'password',
      placeholder: '••••••••',
      required: true,
      isSecret: true,
    },
    setupHint: (
      <ol className="space-y-1.5 text-sm text-content-subtle list-decimal list-inside">
        <li>In Sophos Central, go to <strong>Global Settings → API Credentials</strong>.</li>
        <li>Click <strong>Add Credential</strong>, give it a name, and select <strong>Service Principal</strong>.</li>
        <li>Copy the Client ID and Client Secret. Note your data region (shown in Account Details).</li>
      </ol>
    ),
  },
  {
    kind: 'claude',
    label: 'Claude AI (Anthropic)',
    description: 'Powers ticket classification, skill extraction, and dispatch reasoning.',
    summary: 'Classification · Reasoning · NLP',
    icon: '🤖',
    color: 'bg-amber-100 dark:bg-amber-900/30',
    configFields: [
      {
        key: 'model',
        label: 'Model (optional)',
        type: 'text',
        placeholder: 'claude-sonnet-4-20250514',
        hint: 'Powers Ask-AI. Leave empty for the default model.',
      },
    ],
    secretField: {
      key: 'apiKey',
      label: 'Anthropic API Key',
      type: 'password',
      placeholder: 'sk-ant-••••••••',
      hint: 'Create at console.anthropic.com → API Keys.',
      required: true,
      isSecret: true,
    },
    setupHint: (
      <ol className="space-y-1.5 text-sm text-content-subtle list-decimal list-inside">
        <li>Go to <strong>console.anthropic.com</strong> and sign in.</li>
        <li>Under <strong>API Keys</strong>, click <strong>Create Key</strong>.</li>
        <li>Copy the key — it starts with <code>sk-ant-</code> and is only shown once.</li>
        <li>Set a spending limit under <strong>Billing</strong> to avoid unexpected charges.</li>
      </ol>
    ),
  },
  {
    kind: 'azure_maps',
    label: 'Azure Maps',
    description: 'Travel time and routing for onsite scheduling and dispatch.',
    summary: 'Routing · Travel Time · Geocoding',
    icon: '🗺️',
    color: 'bg-teal-100 dark:bg-teal-900/30',
    configFields: [
      {
        key: 'clientId',
        label: 'Client ID (optional, for AAD auth)',
        type: 'text',
      },
    ],
    secretField: {
      key: 'subscriptionKey',
      label: 'Subscription Key',
      type: 'password',
      placeholder: '••••••••',
      hint: 'Found in Azure Maps account → Authentication.',
      required: true,
      isSecret: true,
    },
    setupHint: (
      <ol className="space-y-1.5 text-sm text-content-subtle list-decimal list-inside">
        <li>In the Azure Portal, create an <strong>Azure Maps</strong> account (S0 or Gen2).</li>
        <li>Under <strong>Authentication</strong>, copy the <strong>Primary Key</strong>.</li>
        <li>Enable the Route, Geocode, and Search APIs under your subscription.</li>
      </ol>
    ),
  },
  {
    kind: 'speech',
    label: 'Azure AI Speech',
    description: 'Transcribes 3CX voicemails for automatic ticket creation.',
    summary: 'Transcription · STT · Voicemail',
    icon: '🎙️',
    color: 'bg-indigo-100 dark:bg-indigo-900/30',
    configFields: [
      {
        key: 'region',
        label: 'Azure Region',
        type: 'text',
        placeholder: 'eastus',
        hint: 'Must match your Speech resource region',
        required: true,
      },
      {
        key: 'endpoint',
        label: 'Custom Endpoint (optional)',
        type: 'url',
        placeholder: 'https://eastus.api.cognitive.microsoft.com',
      },
    ],
    secretField: {
      key: 'subscriptionKey',
      label: 'Speech API Key',
      type: 'password',
      placeholder: '••••••••',
      hint: 'Found under your Azure AI Speech resource → Keys and Endpoint.',
      required: true,
      isSecret: true,
    },
    setupHint: (
      <ol className="space-y-1.5 text-sm text-content-subtle list-decimal list-inside">
        <li>In the Azure Portal, create an <strong>Azure AI Speech</strong> resource.</li>
        <li>Under <strong>Keys and Endpoint</strong>, copy <strong>Key 1</strong> and the region.</li>
        <li>The default region for new resources is <code>eastus</code>.</li>
      </ol>
    ),
  },
  {
    kind: 'telnyx',
    label: 'Telnyx SMS',
    description: 'Programmable SMS: ticket updates, dispatch notifications, and inbound texts.',
    summary: 'SMS · Notifications · Inbound',
    icon: '📱',
    color: 'bg-sky-100 dark:bg-sky-900/30',
    docsUrl: 'https://developers.telnyx.com/docs/messaging',
    configFields: [
      {
        key: 'messagingProfileId',
        label: 'Messaging Profile ID',
        type: 'text',
        placeholder: '00000000-0000-0000-0000-000000000000',
        hint: 'Telnyx Portal → Messaging → Messaging Profiles',
        required: true,
      },
      {
        key: 'fromNumber',
        label: 'From Number (E.164)',
        type: 'text',
        placeholder: '+18885550123',
        hint: 'A number assigned to the messaging profile above',
        required: true,
      },
      {
        key: 'webhookPublicKey',
        label: 'Webhook Public Key (Ed25519, optional)',
        type: 'text',
        hint: 'Telnyx Portal → Developers → Webhook Signatures. Required to accept inbound/delivery webhooks.',
      },
    ],
    secretField: {
      key: 'apiKey',
      label: 'API Key',
      type: 'password',
      placeholder: 'KEY0...',
      hint: 'Telnyx Portal → Developers → API Keys (create a restricted key).',
      required: true,
      isSecret: true,
    },
    setupHint: (
      <ol className="space-y-1.5 text-sm text-content-subtle list-decimal list-inside">
        <li>Sign up at <strong>portal.telnyx.com</strong> and buy or port an SMS-capable number.</li>
        <li>Create a <strong>Messaging Profile</strong> and assign the number to it. Copy the Profile ID.</li>
        <li>Under <strong>Developers → API Keys</strong>, create a key and copy it (starts with <code>KEY</code>).</li>
        <li>(For inbound/delivery webhooks) enable <strong>Webhook Signatures</strong> under Developers and paste the Ed25519 public key above. Point the webhook URL at <code>https://&lt;your-api&gt;/api/v1/sms/webhook</code>.</li>
      </ol>
    ),
  },
];

export const integrationMetaMap = new Map<IntegrationKind, IntegrationMeta>(
  INTEGRATIONS_META.map((m) => [m.kind, m]),
);

export function getIntegrationMeta(kind: IntegrationKind): IntegrationMeta {
  return integrationMetaMap.get(kind) ?? INTEGRATIONS_META[0]!;
}

export { INTEGRATIONS_META };
